import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Star, Shield, MapPin, Truck, Clock, CheckCircle2,
  Award, ArrowLeft, Loader2, Package, Building2, Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";

const TIER_META = {
  bronze: { label: "Bronze", color: "text-amber-700 bg-amber-100 border-amber-300", star: "text-amber-700" },
  silver: { label: "Silver", color: "text-slate-600 bg-slate-100 border-slate-300", star: "text-slate-500" },
  gold: { label: "Gold", color: "text-amber-600 bg-amber-50 border-amber-300", star: "text-amber-500" },
  fortress: { label: "Fortress Partner", color: "text-cyan-700 bg-cyan-50 border-cyan-300", star: "text-cyan-600" },
};

export default function SupplierPublicProfile() {
  const { supplierId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supplier/public/${supplierId}`)
      .then(r => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then(d => setProfile(d.profile))
      .catch(() => setError("Supplier profile not found"))
      .finally(() => setLoading(false));
  }, [supplierId]);

  if (loading) return (<div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center"><Loader2 className="w-10 h-10 text-amber-400 animate-spin" /></div>);
  if (error || !profile) return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center px-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-2">Profile Not Found</h2>
        <p className="text-slate-400 mb-6">This supplier profile doesn&apos;t exist or has been removed.</p>
        <Link to="/"><Button className="bg-[#0056A8] hover:bg-[#003366] text-white">Back to Home</Button></Link>
      </div>
    </div>
  );

  const tier = TIER_META[profile.tier] || TIER_META.bronze;

  return (
    <div className="min-h-screen bg-[#0a0f1a]" data-testid="supplier-public-profile">
      <div className="bg-gradient-to-r from-[#003366] via-[#0056A8] to-[#003366] border-b border-blue-500/20">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-200 hover:text-white text-sm mb-4 transition-colors"><ArrowLeft className="w-4 h-4" /> Back to Network</Link>
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-xl border border-amber-500/30 bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <Truck className="w-10 h-10 text-amber-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-black text-white mb-1" data-testid="supplier-profile-name">{profile.company_name}</h1>
              <p className="text-blue-200 capitalize">{(profile.business_type || "").replace(/_/g, " ")} — {profile.service_area}</p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${tier.color}`}><Star className={`w-3.5 h-3.5 ${tier.star}`} />{tier.label}</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/80 border border-white/20"><MapPin className="w-3 h-3" />{profile.city}, {profile.state}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="supplier-stats">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 text-center">
            <p className="text-2xl font-black text-amber-400">{profile.total_orders}</p>
            <p className="text-xs text-slate-400 mt-1">Orders Fulfilled</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 text-center">
            <p className="text-2xl font-black text-emerald-400">{profile.rating > 0 ? `${profile.rating}/5` : "New"}</p>
            <p className="text-xs text-slate-400 mt-1">Rating</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 text-center">
            <p className="text-2xl font-black text-white capitalize">{profile.tier}</p>
            <p className="text-xs text-slate-400 mt-1">Network Tier</p>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Supplier Details</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3"><Package className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-300">Type: <strong className="text-white capitalize">{(profile.business_type || "").replace(/_/g, " ")}</strong></span></div>
            <div className="flex items-center gap-3"><MapPin className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-300">Service Area: <strong className="text-white">{profile.service_area}</strong></span></div>
            {profile.product_categories && <div className="flex items-center gap-3"><Truck className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-300">Products: <strong className="text-white">{profile.product_categories}</strong></span></div>}
            <div className="flex items-center gap-3"><Calendar className="w-4 h-4 text-slate-500" /><span className="text-sm text-slate-300">Member Since: <strong className="text-white">{profile.member_since ? new Date(profile.member_since).getFullYear() : "2024"}</strong></span></div>
          </div>
        </div>

        <div className="text-center py-6 border-t border-slate-800">
          <p className="text-slate-400 text-sm mb-4">Want to become a supplier in the Sterile Mate network?</p>
          <div className="flex gap-3 justify-center">
            <Link to="/join-network?type=supplier"><Button className="bg-amber-600 hover:bg-amber-700 text-white">Join as Supplier</Button></Link>
            <Link to="/"><Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800">Back to Home</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
